import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { GOOGLE_MAPS_CONFIG } from '../utils/googleMaps';
import { 
  MapPin, AlertTriangle, Users, Calendar, MessageSquare, 
  CheckCircle, Clock, Navigation, Camera, X, ChevronDown,
  Plus, Eye, ClipboardList, Home, Send, Image, Wifi, WifiOff, UserPlus, Link, Share2, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { useChatWebSocket } from '../hooks/useChatWebSocket';

// Image Modal Component for enlarging images
const ImageModal = ({ imageUrl, onClose }) => {
  if (!imageUrl) return null;
  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 400 }}>
      <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: -36, right: 0, background: 'none', border: 'none', color: 'var(--text)', cursor: 'pointer', padding: 4 }}>
          <X size={22} />
        </button>
        <img src={imageUrl} alt="Enlarged view" style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', borderRadius: 12 }}
          onClick={(e) => e.stopPropagation()} />
      </div>
    </div>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgronomistDashboard = () => {
  const { user, getAuthHeaders, logout, token } = useAuth();
  const [activeTab, setActiveTab] = useState('map');
  const [mapCenter, setMapCenter] = useState({ lat: 42.7, lng: 25.5 }); // Bulgaria default
  const [markerPosition, setMarkerPosition] = useState(null);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertForm, setAlertForm] = useState({
    alert_type: 'disease',
    severity: 'observe',
    subtype: '',
    photo_url: null,
    recommendations: ''
  });
  const [subtypes, setSubtypes] = useState({});
  const [cases, setCases] = useState([]);
  const [visitRequests, setVisitRequests] = useState([]);
  const [myAlerts, setMyAlerts] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseMessages, setCaseMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [caseContext, setCaseContext] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [scheduledDate, setScheduledDate] = useState('');
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showResolveConfirm, setShowResolveConfirm] = useState(false);
  const [chatImageUploading, setChatImageUploading] = useState(false);
  const [chatImageUrl, setChatImageUrl] = useState(null);
  const chatFileInputRef = useRef(null);
  const [invites, setInvites] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [newInviteLink, setNewInviteLink] = useState(null);

  // WebSocket for real-time chat
  const handleWsMessage = useCallback((data) => {
    if (data.type === 'new_message' && data.case_id === selectedCase?.id) {
      // Add new message to list without full reload
      setCaseMessages(prev => [...prev, {
        id: data.message_id,
        sender_id: data.sender_id,
        sender_name: data.sender_name,
        sender_role: data.sender_role,
        text: data.text,
        image_urls: data.image_urls || [],
        created_at_utc: data.created_at_utc
      }]);
    }
  }, [selectedCase?.id]);

  const { isConnected: wsConnected } = useChatWebSocket(
    selectedCase?.id,
    token,
    handleWsMessage
  );

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_CONFIG);

  // Fetch subtypes on mount
  useEffect(() => {
    const fetchSubtypes = async () => {
      try {
        const response = await axios.get(`${API}/geo-alerts/subtypes`);
        setSubtypes(response.data);
      } catch (error) {
        console.error('Error fetching subtypes:', error);
      }
    };
    fetchSubtypes();
  }, []);

  // Fetch data
  const fetchCases = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/agronomist/cases`, {
        headers: getAuthHeaders()
      });
      setCases(response.data);
    } catch (error) {
      console.error('Error fetching cases:', error);
    }
  }, [getAuthHeaders]);

  const fetchVisitRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/agronomist/visit-requests`, {
        headers: getAuthHeaders()
      });
      setVisitRequests(response.data);
    } catch (error) {
      console.error('Error fetching visit requests:', error);
    }
  }, [getAuthHeaders]);

  const fetchMyAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/geo-alerts/my-alerts`, {
        headers: getAuthHeaders()
      });
      setMyAlerts(response.data);
    } catch (error) {
      console.error('Error fetching my alerts:', error);
    }
  }, [getAuthHeaders]);

  const fetchInvites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/invites`, {
        headers: getAuthHeaders()
      });
      setInvites(response.data);
    } catch (error) {
      console.error('Error fetching invites:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchCases();
    fetchVisitRequests();
    fetchMyAlerts();
    fetchInvites();
  }, [fetchCases, fetchVisitRequests, fetchMyAlerts, fetchInvites]);

  // Create invite link
  const createInvite = async () => {
    try {
      const response = await axios.post(`${API}/invites`, {}, {
        headers: getAuthHeaders()
      });
      const { token } = response.data;
      const inviteUrl = `${window.location.origin}/register?invite=${token}`;
      setNewInviteLink(inviteUrl);
      setShowInviteModal(true);
      fetchInvites();
      toast.success('Invite link created!');
    } catch (error) {
      toast.error('Failed to create invite');
    }
  };

  // Share invite link
  const shareInviteLink = async (link) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join SFarm Monitor',
          text: 'You have been invited to join SFarm Monitor by your agronomist.',
          url: link
        });
      } catch (error) {
        // User cancelled or share failed - copy to clipboard instead
        copyToClipboard(link);
      }
    } else {
      copyToClipboard(link);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  // Map click handler
  const handleMapClick = (e) => {
    setMarkerPosition({
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    });
  };

  // Use my location
  const useMyLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setMapCenter(pos);
          setMarkerPosition(pos);
          toast.success('Location updated');
        },
        () => {
          toast.error('Could not get your location');
        }
      );
    } else {
      toast.error('Geolocation not supported');
    }
  };

  // Upload photo
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      setAlertForm({ ...alertForm, photo_url: response.data.url });
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  // Create geo alert
  const handleCreateAlert = async () => {
    if (!markerPosition) {
      toast.error('Please place a marker on the map first');
      return;
    }
    if (!alertForm.subtype) {
      toast.error('Please select a specific type (subtype)');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/geo-alerts`, {
        lat: markerPosition.lat,
        lng: markerPosition.lng,
        alert_type: alertForm.alert_type,
        severity: alertForm.severity,
        subtype: alertForm.subtype,
        photo_url: alertForm.photo_url || null,
        recommendations: alertForm.recommendations || null
      }, {
        headers: getAuthHeaders()
      });

      toast.success(`Alert created! Notified ${response.data.farms_notified.high} high-priority and ${response.data.farms_notified.medium} medium-priority farms`);
      setShowAlertModal(false);
      setMarkerPosition(null);
      setAlertForm({ alert_type: 'disease', severity: 'observe', subtype: '', photo_url: null, recommendations: '' });
      fetchMyAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create alert');
    } finally {
      setLoading(false);
    }
  };

  // Claim case
  const handleClaimCase = async (caseId) => {
    try {
      await axios.post(`${API}/cases/${caseId}/claim`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Case claimed!');
      fetchCases();
      setSelectedCase(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim case');
    }
  };

  // Resolve case with confirmation
  const handleResolveCase = async (caseId) => {
    try {
      await axios.post(`${API}/cases/${caseId}/resolve`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Case resolved!');
      fetchCases();
      setSelectedCase(null);
      setShowResolveConfirm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve case');
    }
  };

  // Upload chat image
  const handleChatImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setChatImageUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      setChatImageUrl(response.data.url);
      toast.success('Image ready to send');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setChatImageUploading(false);
    }
  };

  // Load case messages
  const loadCaseMessages = async (caseId) => {
    try {
      const [messagesRes, contextRes] = await Promise.all([
        axios.get(`${API}/cases/${caseId}/messages`, { headers: getAuthHeaders() }),
        axios.get(`${API}/cases/${caseId}/context`, { headers: getAuthHeaders() })
      ]);
      setCaseMessages(messagesRes.data);
      setCaseContext(contextRes.data);
    } catch (error) {
      console.error('Error loading case data:', error);
    }
  };

  // Send message with optional image
  const handleSendMessage = async () => {
    if (!newMessage.trim() && !chatImageUrl) return;
    if (!selectedCase) return;

    try {
      await axios.post(`${API}/cases/${selectedCase.id}/messages`, {
        text: newMessage || '',
        image_urls: chatImageUrl ? [chatImageUrl] : []
      }, {
        headers: getAuthHeaders()
      });
      setNewMessage('');
      setChatImageUrl(null);
      loadCaseMessages(selectedCase.id);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  // Accept visit request
  const handleAcceptVisit = async () => {
    if (!selectedVisit || !scheduledDate) {
      toast.error('Please select a date and time');
      return;
    }

    try {
      await axios.post(`${API}/visit-requests/${selectedVisit.id}/accept`, {
        scheduled_datetime: new Date(scheduledDate).toISOString()
      }, {
        headers: getAuthHeaders()
      });
      toast.success('Visit scheduled!');
      setSelectedVisit(null);
      setScheduledDate('');
      fetchVisitRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept visit');
    }
  };

  // Mark visit as completed
  const handleMarkVisited = async (requestId) => {
    try {
      await axios.post(`${API}/visit-requests/${requestId}/visited`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Visit marked as completed!');
      fetchVisitRequests();
    } catch (error) {
      toast.error('Failed to mark as visited');
    }
  };

  const openCases = cases.filter(c => c.status === 'open');
  const assignedCases = cases.filter(c => c.status === 'assigned' && c.assigned_agronomist_id === user?.id);
  const resolvedCases = cases.filter(c => c.status === 'resolved' && c.assigned_agronomist_id === user?.id);
  const openVisits = visitRequests.filter(v => v.status === 'open');
  const acceptedVisits = visitRequests.filter(v => v.status === 'accepted' && v.assigned_agronomist_id === user?.id);

  return (
    <div className="page-shell">
      {/* Image Modal */}
      <ImageModal imageUrl={enlargedImage} onClose={() => setEnlargedImage(null)} />
      
      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'var(--panel-strong)', backdropFilter: 'blur(14px)', borderBottom: '1px solid var(--border-soft)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 38, height: 38, background: 'linear-gradient(135deg, var(--gold), var(--gold2))', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users style={{ width: 18, height: 18, color: '#0b241a' }} />
            </div>
            <div>
              <h1 style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.2 }}>Agronomist Dashboard</h1>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Welcome, {user?.agronomist_name || 'Agronomist'}</p>
            </div>
          </div>
          <button onClick={logout} className="btn-depth-secondary" style={{ padding: '7px 14px', fontSize: 13 }}>Logout</button>
        </div>
      </header>

      {/* Tabs */}
      <div className="tab-strip">
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 20px' }}>
          <nav style={{ display: 'flex', gap: 0, overflowX: 'auto' }}>
            {[
              { id: 'map', label: 'Map & Alerts', icon: MapPin },
              { id: 'cases', label: `Cases (${openCases.length + assignedCases.length})`, icon: ClipboardList },
              { id: 'visits', label: `Visits (${openVisits.length + acceptedVisits.length})`, icon: Calendar },
              { id: 'invites', label: `Invites (${invites.length})`, icon: UserPlus },
              { id: 'history', label: 'History', icon: CheckCircle }
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`tab-btn${activeTab === tab.id ? ' active' : ''}`}>
                <tab.icon style={{ width: 15, height: 15 }} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main style={{ maxWidth: 1280, margin: '0 auto', padding: '20px 20px' }}>
        {/* Map Tab */}
        {activeTab === 'map' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button onClick={useMyLocation} className="btn-depth-secondary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Navigation style={{ width: 16, height: 16 }} /> Use My Location
              </button>
              <button onClick={() => markerPosition && setShowAlertModal(true)} disabled={!markerPosition}
                className="btn-depth-danger" style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: markerPosition ? 1 : 0.5 }}>
                <AlertTriangle style={{ width: 16, height: 16 }} /> Create Alert
              </button>
              {markerPosition && (
                <span className="badge-depth badge-success" style={{ padding: '8px 14px', fontSize: 13 }}>
                  <MapPin style={{ width: 14, height: 14 }} />
                  Marker: {markerPosition.lat.toFixed(4)}, {markerPosition.lng.toFixed(4)}
                </span>
              )}
            </div>

            {/* Map container */}
            <section className="card-floating" style={{ position: 'relative', padding: 0, overflow: 'hidden', height: 500 }}>
              {isLoaded ? (
                <GoogleMap mapContainerStyle={{ width: '100%', height: '100%' }} center={mapCenter} zoom={10}
                  onClick={handleMapClick} options={{ mapTypeId: 'hybrid', mapTypeControl: true }}>
                  {markerPosition && (
                    <Marker position={markerPosition} icon={{
                      path: window.google?.maps?.SymbolPath?.CIRCLE || 0, scale: 10,
                      fillColor: '#dc2626', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2
                    }} />
                  )}
                  {myAlerts.map(alert => (
                    <Marker key={alert.id} position={{ lat: alert.lat, lng: alert.lng }} icon={{
                      path: window.google?.maps?.SymbolPath?.CIRCLE || 0, scale: 8,
                      fillColor: '#f59e0b', fillOpacity: 0.8, strokeColor: '#fff', strokeWeight: 1
                    }} title={`${alert.alert_type} - ${alert.severity}`} />
                  ))}
                </GoogleMap>
              ) : (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                  Loading map...
                </div>
              )}
            </section>
            {myAlerts.length > 0 && (
              <section className="card-floating" style={{ position: 'relative', padding: 18 }}>
                <h3 style={{ fontWeight: 600, marginBottom: 12 }}>My Recent Alerts</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {myAlerts.slice(0, 5).map(alert => (
                    <div key={alert.id}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 10, background: 'rgba(255,255,255,0.04)', borderRadius: 10, border: '1px solid var(--border-soft)', cursor: 'pointer', transition: 'all 0.12s' }}
                      onClick={() => setSelectedAlert(selectedAlert?.id === alert.id ? null : alert)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <AlertTriangle style={{ width: 18, height: 18, color: alert.severity === 'act_48h' ? 'var(--danger)' : alert.severity === 'check_5d' ? 'var(--warning)' : 'var(--info)' }} />
                        <div>
                          <p style={{ fontWeight: 600, fontSize: 13, textTransform: 'capitalize' }}>{alert.subtype || alert.alert_type.replace('_', ' ')}</p>
                          <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>{new Date(alert.created_at_utc).toLocaleDateString()} - {alert.severity.replace('_', ' ')}</p>
                        </div>
                      </div>
                      {alert.photo_url && (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${alert.photo_url}`} alt=""
                          style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', cursor: 'pointer' }}
                          onClick={(e) => { e.stopPropagation(); setEnlargedImage(`${process.env.REACT_APP_BACKEND_URL}${alert.photo_url}`); }} />
                      )}
                    </div>
                  ))}
                </div>

                {/* Alert Details */}
                {selectedAlert && (
                  <div style={{ marginTop: 14, padding: 14, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.18)', borderRadius: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                      <h4 style={{ fontWeight: 600, color: 'var(--warning)' }}>Alert Details</h4>
                      <button onClick={() => setSelectedAlert(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-muted)' }}>
                        <X style={{ width: 16, height: 16 }} />
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                      {[
                        ['Type', <span style={{ textTransform: 'capitalize' }}>{selectedAlert.alert_type.replace('_', ' ')}</span>],
                        ['Subtype', selectedAlert.subtype || 'N/A'],
                        ['Severity', <span style={{ color: selectedAlert.severity === 'act_48h' ? 'var(--danger)' : selectedAlert.severity === 'check_5d' ? 'var(--warning)' : 'var(--info)' }}>{selectedAlert.severity.replace('_', ' ')}</span>],
                        ['Location', `${selectedAlert.lat?.toFixed(4)}, ${selectedAlert.lng?.toFixed(4)}`],
                        ['Created', new Date(selectedAlert.created_at_utc).toLocaleString()],
                        ['Expires', new Date(selectedAlert.expires_at_utc).toLocaleString()],
                      ].map(([lbl, val]) => (
                        <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: 'var(--text-muted)' }}>{lbl}:</span>
                          <span style={{ fontWeight: 500 }}>{val}</span>
                        </div>
                      ))}
                      {selectedAlert.recommendations && (
                        <div style={{ paddingTop: 6, borderTop: '1px solid var(--border-soft)' }}>
                          <p style={{ color: 'var(--text-muted)', marginBottom: 4 }}>Recommendations:</p>
                          <p style={{ color: 'var(--text-secondary)' }}>{selectedAlert.recommendations}</p>
                        </div>
                      )}
                      {selectedAlert.photo_url && (
                        <div style={{ paddingTop: 6, borderTop: '1px solid var(--border-soft)' }}>
                          <p style={{ color: 'var(--text-muted)', marginBottom: 6 }}>Photo:</p>
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${selectedAlert.photo_url}`} alt="Alert"
                            style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 10, cursor: 'pointer' }}
                            onClick={() => setEnlargedImage(`${process.env.REACT_APP_BACKEND_URL}${selectedAlert.photo_url}`)} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </section>
            )}
          </div>
        )}

        {/* Cases Tab */}
        {activeTab === 'cases' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
            {/* Open Cases — CardCream */}
            <div className="card-floating" style={{ position: 'relative', padding: 18 }}>
              <h3 style={{ fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text)' }}>
                <Clock style={{ width: 20, height: 20, color: '#f59e0b' }} /> Open Cases ({openCases.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {openCases.map(c => (
                  <div key={c.id} onClick={() => { setSelectedCase(c); loadCaseMessages(c.id); }}
                    style={{ padding: 12, border: '1px solid var(--border-soft)', borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s', background: 'rgba(255,255,255,0.04)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div><p style={{ fontWeight: 600, color: 'var(--text)' }}>{c.field_name}</p>
                        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{c.crop}</p></div>
                      <span className={`badge-cream-${c.urgency === 'immediate' ? 'danger' : c.urgency === 'day' ? 'warning' : 'info'}`}
                        style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, height: 'fit-content' }}>{c.urgency}</span>
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{c.description}</p>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>{new Date(c.created_at_utc).toLocaleString()}</p>
                  </div>
                ))}
                {openCases.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 16 }}>No open cases</p>}
              </div>
            </div>

            {/* Assigned Cases — CardCream */}
            <div className="card-floating" style={{ position: 'relative', padding: 18 }}>
              <h3 style={{ fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text)' }}>
                <MessageSquare style={{ width: 20, height: 20, color: 'var(--success)' }} /> My Active Cases ({assignedCases.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {assignedCases.map(c => (
                  <div key={c.id} onClick={() => { setSelectedCase(c); loadCaseMessages(c.id); }}
                    style={{ padding: 12, border: '1px solid rgba(34,197,94,0.25)', background: 'rgba(34,197,94,0.08)', borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div><p style={{ fontWeight: 600, color: 'var(--text)' }}>{c.field_name}</p>
                        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{c.crop}</p></div>
                      <span style={{ padding: '4px 10px', background: 'rgba(34,197,94,0.12)', color: 'var(--success)', borderRadius: 6, fontSize: 12, fontWeight: 600, height: 'fit-content' }}>Assigned</span>
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{c.description}</p>
                  </div>
                ))}
                {assignedCases.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 16 }}>No active cases</p>}
              </div>
            </div>
          </div>
        )}

        {/* Visits Tab */}
        {activeTab === 'visits' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
            {/* Open Visit Requests — CardCream */}
            <div className="card-floating" style={{ position: 'relative', padding: 18 }}>
              <h3 style={{ fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text)' }}>
                <Calendar style={{ width: 20, height: 20, color: '#f59e0b' }} /> Open Requests ({openVisits.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {openVisits.map(v => (
                  <div key={v.id} style={{ padding: 12, border: '1px solid var(--border-soft)', borderRadius: 10, background: 'rgba(255,255,255,0.04)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <p style={{ fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Home style={{ width: 16, height: 16 }} /> {v.farm_name}
                        </p>
                        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Within {v.requested_window}</p>
                      </div>
                      <button onClick={() => setSelectedVisit(v)} className="btn-depth-primary" style={{ padding: '6px 14px', fontSize: 13 }}>Accept</button>
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>Requested: {new Date(v.created_at_utc).toLocaleString()}</p>
                  </div>
                ))}
                {openVisits.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 16 }}>No open visit requests</p>}
              </div>
            </div>

            {/* Scheduled Visits — CardCream */}
            <div className="card-floating" style={{ position: 'relative', padding: 18 }}>
              <h3 style={{ fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text)' }}>
                <CheckCircle style={{ width: 20, height: 20, color: 'var(--success)' }} /> Scheduled Visits ({acceptedVisits.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {acceptedVisits.map(v => (
                  <div key={v.id} style={{ padding: 12, border: '1px solid rgba(34,197,94,0.25)', background: 'rgba(34,197,94,0.08)', borderRadius: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <p style={{ fontWeight: 600, color: 'var(--text)' }}>{v.farm_name}</p>
                        <p style={{ fontSize: 13, color: 'var(--success)' }}>{new Date(v.scheduled_datetime_utc).toLocaleString()}</p>
                      </div>
                      <button onClick={() => handleMarkVisited(v.id)} className="btn-depth-primary" style={{ padding: '6px 14px', fontSize: 13 }}>Mark Visited</button>
                    </div>
                  </div>
                ))}
                {acceptedVisits.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 16 }}>No scheduled visits</p>}
              </div>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="card-floating" style={{ position: 'relative', padding: 18 }}>
            <h3 style={{ fontWeight: 600, marginBottom: 16, color: 'var(--text)' }}>Resolved Cases ({resolvedCases.length})</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {resolvedCases.map(c => (
                <div key={c.id} style={{ padding: 12, border: '1px solid var(--border-soft)', borderRadius: 10, background: 'rgba(255,255,255,0.04)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div><p style={{ fontWeight: 600, color: 'var(--text)' }}>{c.field_name}</p>
                      <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{c.crop}</p></div>
                    <span style={{ padding: '4px 10px', background: 'rgba(255,255,255,0.03)', color: 'var(--text-muted)', borderRadius: 6, fontSize: 12, fontWeight: 600, height: 'fit-content' }}>Resolved</span>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8 }}>{c.description}</p>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>Resolved: {c.resolved_at_utc ? new Date(c.resolved_at_utc).toLocaleString() : 'N/A'}</p>
                </div>
              ))}
              {resolvedCases.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 16 }}>No resolved cases yet</p>}
            </div>
          </div>
        )}

        {/* Invites Tab */}
        {activeTab === 'invites' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card-floating" style={{ position: 'relative', padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h3 style={{ fontWeight: 600, color: 'var(--text)' }}>Invite Farmers</h3>
                <button onClick={createInvite} className="btn-depth-primary" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
                  data-testid="create-invite-btn">
                  <Plus style={{ width: 16, height: 16 }} /> Create Invite Link
                </button>
              </div>
              <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                Generate shareable links to invite farmers to SFarm Monitor. Invited farmers will be linked to your account.
              </p>
            </div>

            <div className="card-floating" style={{ position: 'relative', padding: 18 }}>
              <h3 style={{ fontWeight: 600, marginBottom: 16, color: 'var(--text)' }}>Your Invite Links ({invites.length})</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {invites.map(invite => (
                  <div key={invite.id} style={{ padding: 12, border: '1px solid var(--border-soft)', borderRadius: 10, background: 'rgba(255,255,255,0.04)' }} data-testid={`invite-${invite.id}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Link style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                          <span style={{ fontSize: 14, fontWeight: 600, color: invite.used ? '#aaa' : '#22c55e' }}>
                            {invite.used ? 'Used' : 'Active'}
                          </span>
                        </div>
                        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Created: {new Date(invite.created_at_utc).toLocaleDateString()}</p>
                        <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Expires: {new Date(invite.expires_at_utc).toLocaleDateString()}</p>
                        {invite.used && invite.farmer_email && (
                          <p style={{ fontSize: 12, color: 'var(--success)', marginTop: 4 }}>Used by: {invite.farmer_email}</p>
                        )}
                      </div>
                      {!invite.used && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => shareInviteLink(`${window.location.origin}/register?invite=${invite.token}`)}
                            style={{ padding: 8, background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 8, color: 'var(--text-muted)' }} title="Share">
                            <Share2 style={{ width: 16, height: 16 }} />
                          </button>
                          <button onClick={() => copyToClipboard(`${window.location.origin}/register?invite=${invite.token}`)}
                            style={{ padding: 8, background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 8, color: 'var(--text-muted)' }} title="Copy link">
                            <Copy style={{ width: 16, height: 16 }} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {invites.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 16 }}>No invites created yet. Click "Create Invite Link" to get started.</p>}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Invite Created Modal */}
      {showInviteModal && newInviteLink && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="card-floating anim-scale" style={{ maxWidth: 440, width: '100%', padding: 24 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>Invite Link Created!</h3>
              <button onClick={() => setShowInviteModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, borderRadius: 8 }}>
                <X style={{ width: 20, height: 20 }} /></button>
            </div>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>Share this link with a farmer to invite them to SFarm Monitor:</p>
            <div style={{ background: 'rgba(255,255,255,0.03)', padding: 12, borderRadius: 10, marginBottom: 16, wordBreak: 'break-all', fontSize: 14, color: 'var(--text-secondary)' }}>{newInviteLink}</div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => shareInviteLink(newInviteLink)} className="btn-depth-primary" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Share2 style={{ width: 16, height: 16 }} /> Share</button>
              <button onClick={() => copyToClipboard(newInviteLink)} className="btn-depth-ghost" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Copy style={{ width: 16, height: 16 }} /> Copy</button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Creation Modal */}
      {showAlertModal && (
        <div className="modal-overlay" onClick={() => setShowAlertModal(false)}>
          <div className="card-floating anim-scale" style={{ maxWidth: 440, width: '100%', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>Create Geo Alert</h3>
              <button onClick={() => setShowAlertModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, borderRadius: 8 }}>
                <X style={{ width: 20, height: 20 }} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div><label className="label-depth">Alert Type</label>
                <select value={alertForm.alert_type} onChange={(e) => setAlertForm({ ...alertForm, alert_type: e.target.value, subtype: '' })} className="input-depth">
                  <option value="disease">Disease</option><option value="pest">Pest</option><option value="stress">Crop Stress</option><option value="fertiliser_input_needed">Fertiliser Input Needed</option>
                </select></div>
              <div><label className="label-depth">Specific Type <span style={{ color: '#ef4444' }}>*</span></label>
                <select value={alertForm.subtype} onChange={(e) => setAlertForm({ ...alertForm, subtype: e.target.value })} className="input-depth">
                  <option value="">Select specific type...</option>
                  {(subtypes[alertForm.alert_type] || []).map(st => <option key={st} value={st}>{st}</option>)}
                </select></div>
              <div><label className="label-depth">Severity</label>
                <select value={alertForm.severity} onChange={(e) => setAlertForm({ ...alertForm, severity: e.target.value })} className="input-depth">
                  <option value="observe">Observe</option><option value="check_5d">Check within 5 days</option><option value="act_48h">Act within 48 hours</option>
                </select></div>
              <div><label className="label-depth">Photo <span style={{ color: 'var(--text-muted)' }}>(optional)</span></label>
                <input type="file" ref={fileInputRef} onChange={handlePhotoUpload} accept="image/*" style={{ display: 'none' }} />
                {alertForm.photo_url ? (
                  <div style={{ position: 'relative' }}>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${alertForm.photo_url}`} alt="Alert" style={{ width: '100%', height: 128, objectFit: 'cover', borderRadius: 10 }} />
                    <button onClick={() => setAlertForm({ ...alertForm, photo_url: null })}
                      style={{ position: 'absolute', top: 8, right: 8, padding: 4, background: '#ef4444', color: 'white', borderRadius: 20, border: 'none', cursor: 'pointer' }}>
                      <X style={{ width: 16, height: 16 }} /></button>
                  </div>
                ) : (
                  <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    style={{ width: '100%', padding: 24, border: '2px dashed rgba(0,0,0,0.15)', borderRadius: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, background: 'transparent', cursor: 'pointer' }}>
                    <Camera style={{ width: 24, height: 24, color: 'var(--text-muted)' }} />
                    <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>{uploading ? 'Uploading...' : 'Click to upload photo'}</span>
                  </button>
                )}</div>
              <div><label className="label-depth">Recommendations (optional)</label>
                <textarea value={alertForm.recommendations} onChange={(e) => setAlertForm({ ...alertForm, recommendations: e.target.value })}
                  placeholder="Enter recommendations for farmers..." rows={3} className="input-depth" style={{ resize: 'none' }} /></div>
              <div style={{ padding: 12, background: 'rgba(255,255,255,0.03)', borderRadius: 10 }}>
                <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}><strong>Location:</strong> {markerPosition?.lat.toFixed(4)}, {markerPosition?.lng.toFixed(4)}</p>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>This alert will notify farms within 20km radius</p>
              </div>
              <button onClick={handleCreateAlert} disabled={loading || !alertForm.subtype} className="btn-depth-danger" style={{ width: '100%', padding: '12px 20px', opacity: (loading || !alertForm.subtype) ? 0.5 : 1 }}>
                {loading ? 'Creating...' : 'Create Alert'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Case Detail Modal */}
      {selectedCase && (
        <div className="modal-overlay" onClick={() => { setSelectedCase(null); setCaseContext(null); setChatImageUrl(null); }}>
          <div className="card-floating" style={{ maxWidth: 700, width: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 20, borderBottom: '1px solid rgba(0,0,0,0.08)', flexShrink: 0 }}>
              <div><h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>{selectedCase.field_name}</h3>
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{selectedCase.crop} - {selectedCase.urgency}</p></div>
              <button onClick={() => { setSelectedCase(null); setCaseContext(null); setChatImageUrl(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, borderRadius: 8 }}>
                <X style={{ width: 20, height: 20 }} /></button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ background: 'rgba(255,255,255,0.03)', padding: 16, borderRadius: 10 }}>
                <h4 style={{ fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>Description</h4>
                <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{selectedCase.description}</p>
                {selectedCase.photo_urls?.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    {selectedCase.photo_urls.map((url, i) => (
                      <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt=""
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                        onClick={() => setEnlargedImage(`${process.env.REACT_APP_BACKEND_URL}${url}`)} />
                    ))}
                  </div>
                )}
              </div>

              {caseContext?.field_info && selectedCase.status !== 'open' && (
                <div style={{ background: 'rgba(34,197,94,0.08)', padding: 16, borderRadius: 10, border: '1px solid #bbf7d0' }}>
                  <h4 style={{ fontWeight: 600, marginBottom: 8, color: 'var(--success)' }}>Field Context</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 14 }}>
                    {[['Crop', caseContext.field_info.crop], ['Stage', caseContext.field_info.stage],
                      ['GDD', caseContext.field_info.gdd?.toFixed(1) || '0'], ['DAS', caseContext.field_info.days_after_sowing || '0'],
                      ['Sowing', caseContext.field_info.sowing_date || 'N/A'], ['Area', `${caseContext.field_info.area_ha?.toFixed(1) || '?'} ha`],
                      ['Previous Crop', caseContext.field_info.previous_crop || 'N/A']
                    ].map(([lbl, val]) => (
                      <div key={lbl}><span style={{ color: 'var(--text-muted)' }}>{lbl}:</span> <span style={{ fontWeight: 500 }}>{val}</span></div>
                    ))}
                  </div>
                  
                  {/* NDVI Trend */}
                  {caseContext.ndvi_trend?.readings?.length > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(34,197,94,0.2)' }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--success)', marginBottom: 6 }}>NDVI Trend (Latest: {caseContext.ndvi_trend.latest_value?.toFixed(3) || 'N/A'})</p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {caseContext.ndvi_trend.readings.map((r, i) => (
                          <span key={i} style={{ fontSize: 11, padding: '2px 6px', background: 'rgba(34,197,94,0.15)', borderRadius: 4, color: 'var(--text-secondary)' }}>
                            {r.date}: {r.value?.toFixed(3) || 'N/A'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Recent Activities */}
                  {caseContext.activities?.length > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(34,197,94,0.2)' }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--success)', marginBottom: 4 }}>Recent Activities ({caseContext.activities.length}):</p>
                      {caseContext.activities.slice(0, 5).map((a, i) => (
                        <p key={i} style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 2 }}>
                          <span style={{ color: 'var(--text-muted)' }}>{a.date}:</span> {a.activity_type} {a.product_name ? `(${a.product_name})` : ''} {a.notes ? `- ${a.notes}` : ''}
                        </p>
                      ))}
                    </div>
                  )}
                  
                  {/* Recent Alerts */}
                  {caseContext.recent_alerts?.length > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(34,197,94,0.2)' }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--warning)', marginBottom: 4 }}>Recent Alerts ({caseContext.recent_alerts.length}):</p>
                      {caseContext.recent_alerts.slice(0, 5).map((a, i) => (
                        <p key={i} style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 2 }}>
                          <span style={{ color: 'var(--warning)' }}>{a.key}</span> <span style={{ color: 'var(--text-muted)' }}>- {a.last_triggered?.slice(0, 10)}</span>
                        </p>
                      ))}
                    </div>
                  )}
                  
                  {/* Data availability indicator */}
                  <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid rgba(34,197,94,0.2)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {Object.entries(caseContext.data_available || {}).map(([key, available]) => (
                      <span key={key} style={{ 
                        fontSize: 10, padding: '2px 6px', borderRadius: 4,
                        background: available ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                        color: available ? 'var(--success)' : 'var(--danger)'
                      }}>
                        {key}: {available ? '✓' : '✗'}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Chat */}
              {selectedCase.status === 'assigned' && (
                <div style={{ border: '1px solid var(--border-soft)', borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ padding: 12, background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid var(--border-soft)' }}>
                    <h4 style={{ fontWeight: 600, color: 'var(--text)' }}>Chat</h4>
                  </div>
                  <div style={{ height: 200, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {caseMessages.map(msg => (
                      <div key={msg.id} style={{
                        padding: 8, borderRadius: 10, maxWidth: '80%',
                        background: msg.sender_role === 'agronomist' ? '#dcfce7' : '#e5e7eb',
                        marginLeft: msg.sender_role === 'agronomist' ? 'auto' : 0,
                      }}>
                        <p style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{msg.sender_name}</p>
                        {msg.text && <p style={{ fontSize: 14, color: '#1f2937' }}>{msg.text}</p>}
                        {msg.image_urls?.length > 0 && (
                          <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                            {msg.image_urls.map((url, i) => (
                              <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt=""
                                style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                                onClick={() => setEnlargedImage(`${process.env.REACT_APP_BACKEND_URL}${url}`)} />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    {caseMessages.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>No messages yet</p>}
                  </div>
                  <div style={{ padding: 12, borderTop: '1px solid var(--border-soft)', display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                    {chatImageUrl && (
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${chatImageUrl}`} alt="To send"
                          style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8 }} />
                        <button onClick={() => setChatImageUrl(null)}
                          style={{ position: 'absolute', top: -8, right: -8, padding: 2, background: '#ef4444', color: 'white', borderRadius: 20, border: 'none', cursor: 'pointer' }}>
                          <X style={{ width: 12, height: 12 }} /></button>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input type="file" ref={chatFileInputRef} onChange={handleChatImageUpload} accept="image/*" style={{ display: 'none' }} />
                      <button onClick={() => chatFileInputRef.current?.click()} disabled={chatImageUploading}
                        style={{ padding: 10, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 8, background: 'rgba(255,255,255,0.04)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Attach image">
                        {chatImageUploading ? <div className="spinner" style={{ width: 20, height: 20 }} /> : <Image style={{ width: 20, height: 20, color: 'var(--text-muted)' }} />}
                      </button>
                      <input type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..." className="input-depth" style={{ flex: 1 }}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()} />
                      <button onClick={handleSendMessage} disabled={!newMessage.trim() && !chatImageUrl}
                        className="btn-depth-primary" style={{ padding: '10px 16px', opacity: (!newMessage.trim() && !chatImageUrl) ? 0.5 : 1 }}>
                        <Send style={{ width: 20, height: 20 }} /></button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{ padding: 16, borderTop: '1px solid var(--border-soft)', display: 'flex', gap: 12, flexShrink: 0 }}>
              {selectedCase.status === 'open' && (
                <button onClick={() => handleClaimCase(selectedCase.id)} className="btn-depth-primary" style={{ flex: 1 }}>Claim Case</button>
              )}
              {selectedCase.status === 'assigned' && (
                <button onClick={() => setShowResolveConfirm(true)} className="btn-depth-primary" style={{ padding: '8px 16px', fontSize: 13, background: '#3B82F6' }}>Resolve</button>
              )}
              <button onClick={() => { setSelectedCase(null); setCaseContext(null); setChatImageUrl(null); }} className="btn-depth-ghost" style={{ padding: '8px 24px' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Confirmation */}
      {showResolveConfirm && selectedCase && (
        <div className="modal-overlay" style={{ zIndex: 310 }} onClick={() => setShowResolveConfirm(false)}>
          <div className="card-floating anim-scale" style={{ maxWidth: 380, width: '100%', padding: 24 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Resolve Case?</h3>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>Are you sure you want to resolve this case? This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setShowResolveConfirm(false)} className="btn-depth-ghost" style={{ flex: 1 }}>Cancel</button>
              <button onClick={() => handleResolveCase(selectedCase.id)} className="btn-depth-primary" style={{ flex: 1, background: '#3B82F6' }}>Yes, Resolve</button>
            </div>
          </div>
        </div>
      )}

      {/* Visit Accept Modal */}
      {selectedVisit && (
        <div className="modal-overlay" onClick={() => setSelectedVisit(null)}>
          <div className="card-floating anim-scale" style={{ maxWidth: 440, width: '100%', padding: 24 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>Schedule Visit</h3>
              <button onClick={() => setSelectedVisit(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, borderRadius: 8 }}>
                <X style={{ width: 20, height: 20 }} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ padding: 12, background: 'rgba(255,255,255,0.03)', borderRadius: 10 }}>
                <p style={{ fontWeight: 600, color: 'var(--text)' }}>{selectedVisit.farm_name}</p>
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Requested: within {selectedVisit.requested_window}</p>
              </div>
              <div><label className="label-depth">Select Date & Time</label>
                <input type="datetime-local" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="input-depth" /></div>
              <button onClick={handleAcceptVisit} disabled={!scheduledDate} className="btn-depth-primary"
                style={{ width: '100%', padding: '12px 20px', opacity: scheduledDate ? 1 : 0.5 }}>Confirm Visit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgronomistDashboard;
